/**
 * activationComposerViewModel — `(reduxState, {raw, fromLink, draft}) → props de <ActivationComposerPage>`.
 *
 * DEUX portes, une surface (`ActivationComposerScreen.js:225-262`) :
 * - le LIEN SIGNÉ : la réponse du lien fait foi. Un navigateur jamais connecté n'a ni `formData`
 *   ni `staticData` — et surtout, Redux est persisté (`persist:root`) : une grille tarifaire figée
 *   à la dernière visite gagnerait sur celle que le serveur vient de calculer pour CE lien, et le
 *   parent lirait l'ancien prix en se faisant facturer le nouveau ;
 * - le PORTAIL connecté : Redux porte déjà tout, aucun fetch.
 *
 * Le brouillon (`draft`) est une ENTRÉE, pas une échappatoire : la fonction reste pure et
 * synchrone. C'est lui qui fait du récapitulatif un prix recalculé à chaque geste.
 */
import { createSelector } from "reselect"

import {
    toActivationComposerModel,
    type ActivationComposerModel,
    type ActivationComposerSources,
    type ActivationDraft,
    type ApiActivationLinkContext,
} from "../../../adapters/parent/Activation/activationAdapter"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type ActivationComposerViewModelInput = {
    raw: ApiActivationLinkContext | null
    fromLink: boolean
    draft: ActivationDraft
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

/**
 * Le contexte du composeur, résolu depuis la porte qui l'a ouvert.
 *
 * ⚠️ `mapStateToProps` étale `currentUser.object` SUR `formData.user` : c'est le premier qui porte
 * `upcoming_activation`, le second le profil (donc la classe). L'ordre est reproduit tel quel.
 */
export const readActivationComposerSources = createSelector(
    [
        (state: ReduxState) => state?.data?.formData?.user,
        (state: ReduxState) => selectDisplayedStudent(state),
        (state: ReduxState) => state?.data?.formData?.schoolSubjects,
        (state: ReduxState) => state?.data?.formData?.pricingTableV2,
        (state: ReduxState) => state?.data?.formData?.selfServeNoCommitmentHours,
        (state: ReduxState) => state?.data?.formData?.sessionsPerWeek,
        (state: ReduxState) => state?.data?.staticData?.summer_config,
        (state: ReduxState) => state?.data?.staticData?.server_timezone,
        (_state: ReduxState, raw: ApiActivationLinkContext | null) => raw,
        (_state: ReduxState, _raw: ApiActivationLinkContext | null, fromLink: boolean) => fromLink,
    ],
    (
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        formDataUser: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        currentUserObject: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        schoolSubjects: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        pricingTableV2: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        selfServeNoCommitmentHours: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        sessionsPerWeek: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        summerConfig: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        serverTimezone: any,
        raw,
        fromLink
    ): ActivationComposerSources => {
        const reduxUser = {
            ...(formDataUser ?? {}),
            ...(currentUserObject ?? {}),
            profile: currentUserObject?.profile ?? formDataUser?.profile,
        }

        return {
            activation: (fromLink ? raw?.upcoming_activation : reduxUser?.upcoming_activation) ?? null,
            studentClass: (fromLink ? raw?.student?.class : reduxUser?.profile?.class) ?? null,
            schoolSubjects: (fromLink ? raw?.school_subjects : schoolSubjects) ?? [],
            pricingTableV2: (fromLink ? raw?.pricing_table_v2 : pricingTableV2) ?? null,
            selfServeNoCommitmentHours:
                (fromLink ? raw?.self_serve_no_commitment_hours : selfServeNoCommitmentHours) ?? null,
            sessionsPerWeek: (fromLink ? raw?.sessions_per_week : sessionsPerWeek) ?? null,
            summerConfig: (fromLink ? raw?.summer_config : summerConfig) ?? null,
            fromLink,
            // Lus sur Redux quelle que soit la porte, comme le legacy (`reduxUser.timezone`,
            // `staticData.server_timezone`) : la charge du lien n'en porte pas. Vides pour un
            // visiteur jamais connecté — la grille reste alors en heures de Paris.
            userTimezone: reduxUser?.timezone ?? null,
            serverTimezone: serverTimezone ?? null,
        }
    }
)

export type ActivationComposerViewModel = ActivationComposerModel & {
    /** Entrée par lien : le contexte n'est pas encore là → attente sobre, comme l'arrivée. */
    loading: boolean
    /** L'envoi est en vol — les deux services, comme le legacy les lit tous les deux. */
    submitting: boolean
}

export const activationComposerViewModel = createSelector(
    [
        // Les trois entrées de l'écran sont lues UNE À UNE : il compose son littéral à chaque
        // rendu, et comparer l'objet remettrait le view-model au travail à chaque action.
        (state: ReduxState, input: ActivationComposerViewModelInput) =>
            readActivationComposerSources(state, input.raw, input.fromLink),
        (_state: ReduxState, input: ActivationComposerViewModelInput) => input.draft,
        (_state: ReduxState, input: ActivationComposerViewModelInput) => input.raw,
        (_state: ReduxState, input: ActivationComposerViewModelInput) => input.fromLink,
        (state: ReduxState) => state?.api?.activateSubscriptionSuite?.request,
        (state: ReduxState) => state?.api?.activateSubscriptionSuiteFromLink?.request,
    ],
    (sources, draft, raw, fromLink, suiteRequesting, fromLinkRequesting): ActivationComposerViewModel => ({
        ...toActivationComposerModel(sources, draft),
        loading: fromLink && raw == null,
        submitting: !!suiteRequesting || !!fromLinkRequesting,
    })
)
